/**
 * API client for the feature-flag system, proxied through pro-api to user-service.
 */

import { authenticatedProApiFetch } from './pro-api.client';

export type FeatureFlagType = 'boolean' | 'string' | 'number' | 'enum';
export type OrgConflictStrategy = 'most_permissive' | 'most_restrictive' | 'primary_org';
export type OrgAdminAccess = 'hidden' | 'read_only' | 'editable';

export interface FeatureFlag {
  id: string;
  key: string;
  label: string;
  description?: string | null;
  type: FeatureFlagType;
  options?: string[] | null;
  value: string;
  orgConflictStrategy: OrgConflictStrategy;
  orgAdminAccess: OrgAdminAccess;
  groupName: string | null;
  overridable: boolean;
  overrideCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlagOverride {
  id: string;
  userId: string;
  value: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string | null;
    googleAvatarUrl?: string | null;
  };
}

export interface FeatureFlagOrganizationOverride {
  id: string;
  organizationId: string;
  value: string;
  organization?: { id: string; name: string; logoUrl?: string | null };
}

export interface FeatureFlagDetail extends FeatureFlag {
  overrides: FeatureFlagOverride[];
  organizationOverrides: FeatureFlagOrganizationOverride[];
}

export interface OrganizationFeatureFlagRow extends FeatureFlag {
  overrideValue: string | null;
  canEdit: boolean;
}

export interface UserFeatureFlagOverride {
  flagId: string;
  flagKey: string;
  value: string;
}

export interface ResolvedFlag {
  key: string;
  type: FeatureFlagType;
  options?: string[] | null;
  value: string;
  isOverridden: boolean;
  source: 'user' | 'organization' | 'global';
  organizationId?: string;
}

export async function fetchFeatureFlags(token: string): Promise<FeatureFlag[]> {
  return authenticatedProApiFetch<FeatureFlag[]>('/feature-flags', token);
}

export async function fetchFeatureFlag(token: string, id: string): Promise<FeatureFlagDetail> {
  return authenticatedProApiFetch<FeatureFlagDetail>(`/feature-flags/${id}`, token);
}

export async function createFeatureFlag(
  token: string,
  data: {
    key: string;
    label: string;
    description?: string;
    type: FeatureFlagType;
    options?: string[];
    value: string;
    orgConflictStrategy?: OrgConflictStrategy;
    orgAdminAccess?: OrgAdminAccess;
    groupName?: string | null;
    overridable?: boolean;
  },
): Promise<FeatureFlag> {
  return authenticatedProApiFetch<FeatureFlag>('/feature-flags', token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFeatureFlag(
  token: string,
  id: string,
  data: Partial<{
    label: string;
    description: string | null;
    type: FeatureFlagType;
    options: string[] | null;
    value: string;
    orgConflictStrategy: OrgConflictStrategy;
    orgAdminAccess: OrgAdminAccess;
    groupName: string | null;
    overridable: boolean;
  }>,
): Promise<FeatureFlag> {
  return authenticatedProApiFetch<FeatureFlag>(`/feature-flags/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteFeatureFlag(token: string, id: string): Promise<void> {
  await authenticatedProApiFetch(`/feature-flags/${id}`, token, { method: 'DELETE' });
}

export async function upsertFeatureFlagOverride(
  token: string,
  flagId: string,
  userId: string,
  value: string,
): Promise<FeatureFlagOverride> {
  return authenticatedProApiFetch<FeatureFlagOverride>(`/feature-flags/${flagId}/overrides`, token, {
    method: 'PUT',
    body: JSON.stringify({ userId, value }),
  });
}

export async function deleteFeatureFlagOverride(
  token: string,
  flagId: string,
  userId: string,
): Promise<void> {
  await authenticatedProApiFetch(`/feature-flags/${flagId}/overrides/${userId}`, token, {
    method: 'DELETE',
  });
}

export async function fetchUserFeatureFlagOverrides(
  token: string,
  userId: string,
): Promise<UserFeatureFlagOverride[]> {
  return authenticatedProApiFetch<UserFeatureFlagOverride[]>(
    `/feature-flags/overrides?userId=${encodeURIComponent(userId)}`,
    token,
  );
}

export async function upsertOrganizationFeatureFlagOverride(
  token: string,
  flagId: string,
  organizationId: string,
  value: string,
): Promise<FeatureFlagOrganizationOverride> {
  return authenticatedProApiFetch<FeatureFlagOrganizationOverride>(
    `/feature-flags/${flagId}/organization-overrides`,
    token,
    { method: 'PUT', body: JSON.stringify({ organizationId, value }) },
  );
}

export async function deleteOrganizationFeatureFlagOverride(
  token: string,
  flagId: string,
  organizationId: string,
): Promise<void> {
  await authenticatedProApiFetch(
    `/feature-flags/${flagId}/organization-overrides/${organizationId}`,
    token,
    { method: 'DELETE' },
  );
}

export async function fetchOrganizationFeatureFlags(
  token: string,
  organizationId: string,
): Promise<OrganizationFeatureFlagRow[]> {
  return authenticatedProApiFetch<OrganizationFeatureFlagRow[]>(
    `/feature-flags/organization-overrides?organizationId=${encodeURIComponent(organizationId)}`,
    token,
  );
}

export async function fetchResolvedFeatureFlags(token: string): Promise<ResolvedFlag[]> {
  const res = await authenticatedProApiFetch<{ flags: ResolvedFlag[] }>(
    '/feature-flags/evaluate',
    token,
  );
  return res.flags;
}
